import React, { useEffect, useState } from "react";
import { ForumPost } from "protogen/forum_service_pb";
import { useEditPostLike } from "services/forum";
import { Box, Typography } from "@mui/material";
import { Heart, MessageSquare } from "lucide-react";
import LikerTooltip from "./LikerTooltip";
import { pluralize } from "../../common/utils";
import AnimatedIcon from "../common/AnimatedIcon";

export default ({
  post,
  onCommentClick,
  compact = false,
  hideComments = false,
}: {
  post: ForumPost;
  onCommentClick?: () => void;
  compact?: boolean;
  hideComments?: boolean;
}) => {
  const [postLikes, setPostLikes] = useState<number>(0);
  const [isLikedByUser, setIsLikedByUser] = useState<boolean>(false);

  const { request } = useEditPostLike((r) => {
    if (r.isLiked === undefined) return;
    setPostLikes((prev) => (r.isLiked ? prev + 1 : prev - 1));
    setIsLikedByUser(r.isLiked);
  });
  const handleEditCommentLike = () => {
    // Do this optimisitally to cut network latency.
    setIsLikedByUser((prev) => !prev);
    request({
      postRef: post.ref,
      isLiked: !isLikedByUser,
    });
  };

  useEffect(() => {
    setPostLikes(post.totalLikes);
    setIsLikedByUser(post.isLikedByUser);
  }, []);

  const iconSize = compact ? 18 : 24;
  const textVariant = compact ? "bodySmall" : "body";

  return (
    <Box
      display="flex"
      flexDirection="row"
      gap={compact ? "8px" : "12px"}
      width={{ xs: "100%", md: "auto" }}
    >
      <LikerTooltip likers={post.likerHints} total={postLikes}>
        <Box
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleEditCommentLike();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#6B6E7B",
            cursor: "pointer",
          }}
        >
          <AnimatedIcon
            icon={Heart}
            isActive={isLikedByUser}
            aria-label="Like"
            size={iconSize}
            activeColor={"#EF7B77"}
            inactiveColor={"#8E9598"}
          />
          {!compact && (
            <Typography
              variant={textVariant}
              sx={{
                marginLeft: "4px",
                color: "#616161",
              }}
            >
              {postLikes} {pluralize("like", postLikes)}
            </Typography>
          )}
          {compact && postLikes > 0 && (
            <Typography
              variant={textVariant}
              sx={{
                marginLeft: "2px",
                color: "#616161",
              }}
            >
              {postLikes}
            </Typography>
          )}
        </Box>
      </LikerTooltip>
      {!hideComments && (
        <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <Box
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCommentClick?.();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#6B6E7B",
              cursor: onCommentClick ? "pointer" : undefined,
            }}
          >
            <MessageSquare size={`${iconSize}px`} stroke="#8E9598" />
          </Box>
          {!compact && (
            <Typography
              variant={textVariant}
              sx={{
                marginLeft: "4px",
                color: "#616161",
              }}
            >
              {post.commentCount} {post.commentCount == 1 ? "reply" : "replies"}
            </Typography>
          )}
          {compact && post.commentCount > 0 && (
            <Typography
              variant={textVariant}
              sx={{
                marginLeft: "2px",
                color: "#616161",
              }}
            >
              {post.commentCount}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
